import { t } from "@lingui/core/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { RefObject, useCallback, useState } from "react";
import { LayoutChangeEvent, Platform, ScrollView } from "react-native";

import { updateDiningNotes } from "@/features/BookingWizard/DiningBookingWizard/atoms/actions/updateDiningNotes/updateDiningNotes";
import { getDiningNotes } from "@/features/BookingWizard/DiningBookingWizard/atoms/selectors/getDiningNotes/getDiningNotes";
import { TextArea } from "@whoosh/design-system/src/components/controls/TextArea";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

type Props = {
  scrollViewRef: RefObject<ScrollView | null>;
};

export const DiningWizardNotesControl = ({ scrollViewRef }: Props) => {
  const notes = useAtomValue(getDiningNotes);
  const setNotes = useSetAtom(updateDiningNotes);
  const [notesLayoutY, setNotesLayoutY] = useState(0);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setNotesLayoutY(event.nativeEvent.layout.y);
  }, []);

  /**
   * This is so that on mobile -- when you click the notes field and the keyboard pops up
   * we give it a moment (100ms) to settle because it'll squish our container and create a need to scroll.
   * This then scrolls the field into view.
   *
   * A normal keyboard avoiding view is wrapped aroudn the whole modal -- keeping the 'continue' button in view.
   * So this extra measure was necessary
   */
  const handleFocus = useCallback(() => {
    if (Platform.OS === "web") {
      return;
    }
    const targetY = Math.max(notesLayoutY - 16, 0);
    setTimeout(() => {
      scrollViewRef.current?.scrollTo({ y: targetY, animated: true });
    }, 100);
  }, [notesLayoutY, scrollViewRef]);

  return (
    <VStack gap={4} backgroundColor="surface-primary" onLayout={handleLayout}>
      <Text variant="mainBaseSemiBold">{t`Any special requests?`}</Text>
      <TextArea
        name="dining-notes"
        placeholder={t`Let us know if you have any special requests`}
        value={notes}
        size="large"
        onFocus={handleFocus}
        onChange={(text) => {
          setNotes(text);
        }}
      />
    </VStack>
  );
};
